import { IExamsListFilters } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

const ExamService = sdk.getService('ExamService');

interface IStore {
  page: number;
  limit: number;
  search: string;
  filter?: IExamsListFilters;
  setFilter: (filter?: IExamsListFilters) => void;
  setPageAndLimit: (page: number, limit: number) => void;
  setSearch: (search: string) => void;
}

const store = createStore<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  setFilter: (filter?: IExamsListFilters) =>
    set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit })),
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE }))
}));

export const useExamsList = () => {
  const { page, limit, filter, search, setFilter, setSearch, setPageAndLimit } = store();

  const queryKey = ['exams', `page-${page}`, `limit-${limit}`, search, filter];
  const { isLoading, data, error } = useQuery({
    queryKey,
    queryFn: () => ExamService.findAllExams({ page, limit }, search, filter)
  });

  const resetSearchResults = () => setSearch('');

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  return {
    exams: data?.docs,
    isLoading,
    error,
    setPageAndLimit,
    pagination,
    filter,
    setFilter,
    setSearch,
    resetSearchResults
  };
};
